/**
 * ThemeSwitch.tsx — segmented three-way System · Light · Dark control.
 *
 * Lifted from apps/chatter-app/src/lib/theme.tsx (D-02).
 *
 * A11y: role="radiogroup" + role="radio" + roving arrow-key navigation
 * (WCAG 2.1.1 APG radio-button pattern). Focus ring is keyboard-only
 * (focus-visible:) matching the project convention.
 */

import * as React from 'react';
import { useTheme } from './ThemeProvider.js';
import { arrowDirection, focusRovingSibling } from '../lib/keyboard.js';
import type { ThemeMode } from './ThemeProvider.js';

const OPTIONS: { value: ThemeMode; label: string; icon: React.ReactNode }[] = [
  {
    value: 'system',
    label: 'System',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5">
        <rect x="2" y="4" width="20" height="13" rx="2" />
        <path strokeLinecap="round" d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
  {
    value: 'light',
    label: 'Light',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5">
        <circle cx="12" cy="12" r="4" />
        <path strokeLinecap="round" d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </svg>
    ),
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3.5 h-3.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
      </svg>
    ),
  },
];

export function ThemeSwitch() {
  const { mode, setMode } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="inline-flex items-center gap-0.5 rounded-md border border-border bg-bg-card p-0.5"
    >
      {OPTIONS.map((opt) => {
        const active = mode === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.label}
            title={opt.label}
            tabIndex={active ? 0 : -1}
            onClick={() => setMode(opt.value)}
            onKeyDown={(e) => {
              const dir = arrowDirection(e.key);
              if (!dir) return;
              e.preventDefault();
              // Radio pattern: arrow moves focus AND selects the focused option.
              const next = focusRovingSibling(e.currentTarget.parentElement, e.currentTarget, dir);
              const nextMode = next?.getAttribute('aria-label')?.toLowerCase();
              if (nextMode === 'system' || nextMode === 'light' || nextMode === 'dark') {
                setMode(nextMode);
              }
            }}
            className={[
              'flex items-center justify-center w-7 h-7 rounded transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
              active
                ? 'bg-accent text-white'
                : 'text-text-muted hover:text-text hover:bg-bg-hover',
            ].join(' ')}
          >
            {opt.icon}
          </button>
        );
      })}
    </div>
  );
}
